import React from 'react';
import { alpha, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { CaptainDTO } from '../../types';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { useItemPopover } from '../../hooks/useItemPopover';
import { ItemDetailsPopover } from './ItemDetailsPopover';
import { ManageCaptainLevelsModal } from '../modals/ManageCaptainLevelsModal';
import {
  getCaptainLevel,
  getCaptainSharePercent,
  getUnusedCaptainLevelUps,
} from '../../utils/captainRules';

interface CaptainDetailsProps {
  captain: CaptainDTO;
  editMode?: boolean;
  onLevelUp?: () => void;
}

/** Captain-specific chrome: level, XP, treasure share and tricks of the trade. */
export const CaptainDetails: React.FC<CaptainDetailsProps> = ({ captain, editMode = false, onLevelUp }) => {
  const tricks = useReferenceDataStore((s) => s.tricks);
  const { popover, handleItemClick, closePopover } = useItemPopover();
  const [isManageLevelsOpen, setIsManageLevelsOpen] = React.useState(false);
  const level = getCaptainLevel(captain.advances);
  const unusedLevelUps = getUnusedCaptainLevelUps(captain);
  const captainTricks = captain.trickIds
    .map((id) => tricks.find((t) => t.id === id))
    .filter((t): t is NonNullable<typeof t> => t !== undefined);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Tooltip title="Manage captain levels">
          <Chip
            size="small"
            label={`Level ${level}`}
            color="primary"
            variant="outlined"
            onClick={() => setIsManageLevelsOpen(true)}
          />
        </Tooltip>
        <Chip size="small" label={`${captain.experience} XP`} variant="outlined" />
        <Tooltip title="The captain's share of all gold acquired (level + 10%)">
          <Chip size="small" label={`Share: ${getCaptainSharePercent(level)}%`} variant="outlined" />
        </Tooltip>
        {editMode && unusedLevelUps > 0 && onLevelUp && (
          <Chip
            size="small"
            icon={<FileUploadIcon fontSize="small" />}
            label={`${unusedLevelUps}`}
            onClick={onLevelUp}
            sx={{
              backgroundColor: alpha("#FFD700", 0.55),
              ":hover": {
                backgroundColor: alpha("#FFD700", 0.75),
              },
            }}
          />
        )}
      </Stack>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Tricks of the Trade
        </Typography>
        {captainTricks.length > 0 ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {captainTricks.map((trick) => (
              <Chip
                key={trick.id}
                size="small"
                label={trick.name}
                variant="outlined"
                color="secondary"
                onClick={(e) =>
                  handleItemClick(e, {
                    name: trick.name,
                    description: `${trick.effect} — Declare: ${trick.whenToDeclare}`,
                    specialTags: [],
                  })
                }
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No tricks known
          </Typography>
        )}
      </Box>
      <ItemDetailsPopover popover={popover} onClose={closePopover} />
      <ManageCaptainLevelsModal
        open={isManageLevelsOpen}
        captain={captain}
        onClose={() => setIsManageLevelsOpen(false)}
      />
    </Box>
  );
};
